// React
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
// ライブラリ
import dayjs from 'dayjs';
import Swal from 'sweetalert2';
// コンポーネント
import SkillTagFilter from '../../../components/input/SkillTagFilter/SkillTagFilter';
// カスタムフック
import { useSkillTags } from '../../../hooks/useSkillTags';
// ユーティリティ
import { menu } from '../../../constants';
import { errorMessages } from '../../../utils/messagesUtils';

import sty from './EngineerList.module.css';

const EngineerListByLoggedInAsEngineer = () => {
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedTags, setSelectedTags] = useState([]);
    const [appliedTags, setAppliedTags] = useState([]); // 実際の検索に適用されるタグ
    const [tagCategories, setTagCategories] = useState({});

    const { transformSkillTagsData, filterEngineers } = useSkillTags({
        selectedTags,
        setSelectedTags,
        tagCategories,
        mode: 'filter'
    });

    const fetchData = async () => {
        setIsLoading(true);
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/engineers`, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                },
                credentials: 'include'
            });
            const result = await response.json();
            if (response.status === 200 && result.status === 'OK') {
                if(result.data === null){
                    const error = new Error();
                    error.front_message = errorMessages.common.data_not_found;
                    throw error;
                } else {
                    setData(result.data);
                }
            } else {
                if (response.status === 401) {
                    navigate(menu.ENGINEER.SIGNIN.PATH);
                } else if(response.status === 404) {
                    const error = new Error();
                    error.front_message = errorMessages.common.data_not_found;
                    throw error;
                } else {
                    const error = new Error();
                    error.front_message = errorMessages.common.data_fetch_failed;
                    throw error;
                }
            }
        } catch (e) {
            setData([]);
            await Swal.fire({
                icon: 'error',
                html: `${e.front_message || errorMessages.api.server_error}`,
                customClass: {
                    htmlContainer: 'large-text'
                }
            })
        } finally {
            setIsLoading(false);
        }
    };

    const fetchSkillTagsData = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/skill-tags?userType=engineer`, {
                method: "GET",
                headers: {
                    'Accept': "application/json",
                    "Content-Type": "application/json",
                },
                credentials: 'include'
            });
            const result = await response.json();
            if (response.status === 200 && result.status === 'OK') {
                setTagCategories(transformSkillTagsData(result.data));
            } else {
                if (response.status === 401) {
                    navigate(menu.ENGINEER.SIGNIN.PATH)
                } else {
                    const error = new Error();
                    error.front_message = errorMessages.common.data_fetch_failed;
                    throw error;
                }
            }
        } catch (e) {
            await Swal.fire({
                icon: 'error',
                html: `${e.front_message || errorMessages.api.server_error}`,
                customClass: {
                    htmlContainer: 'large-text'
                }
            })
        }
    }

    useEffect(() => {
        fetchData();
        fetchSkillTagsData();
    }, []);

    // 検索実行ハンドラー
    const handleSearch = (tagsToApply = null) => {
        const tagsToUse = tagsToApply !== null ? tagsToApply : selectedTags;
        setAppliedTags([...tagsToUse]);
    };

    // データフィルタリング
    const filteredData = filterEngineers(data, appliedTags);

    // 全ての日付を比較して最新の更新日を返す
    const getLatestUpdateDate = (engineer) => {
        const dates = [
            engineer?.profile?.updatedAt,
            engineer?.certifications_updatedAt,
            engineer?.projects_updatedAt,
            engineer?.works_updatedAt
        ].filter(date => date); // null や undefined を除外

        if (dates.length === 0) return null;

        return dates.reduce((latest, current) => {
            return dayjs(current).isAfter(dayjs(latest)) ? current : latest;
        });
    };

    // 名前表示の関数
    const getDisplayName = (engineer) => {
        const lastNama = engineer?.profile?.last_name
        const firstName = engineer?.profile?.first_name;
        if((lastNama == '' || lastNama == null) && (firstName == '' || firstName == null)){
            return engineer?.email;
        } else {
            return `${lastNama} ${firstName}`;
        }
    };

    // 遷移先のパス指定
    const viewPath = '/engineers';
    
    return (
        <div className={`bg ${sty['bg--engineerlist-sp']}`}>
            <div className="page-tit">
                <div className="page-tit-outer">
                    <p className="page-tit-txt">BLOWISH</p>
                    <p className="page-tit-txt">エンジニア一覧</p>
                    <div className="page-tit-border"></div>
                </div>
            </div>

            <SkillTagFilter 
                selectedTags={selectedTags} 
                setSelectedTags={setSelectedTags} 
                tagCategories={tagCategories}
                onSearch={handleSearch}
            />

            <div className={sty['list-pc']}>
                <div className={sty['list-pc__table']}>
                    <div className={sty['list-pc__tr-h']}>
                        <div className={sty['list-pc__th']}>氏名</div>
                        <div className={sty['list-pc__th']}>職種分類</div>
                        <div className={sty['list-pc__th']}>スキルタグ</div>
                        <div className={sty['list-pc__th']}>更新日時</div>
                    </div>

                    {isLoading ? (
                        <div style={{ textAlign: 'center', padding: '40px 0', fontSize: '1.2em' }}>
                            Loading...
                        </div>
                    ) : filteredData.length > 0 ? (
                        filteredData.map((engineer, index) => {
                            const hasSkillsheet = engineer?.skillsheetBox?.[0]?.skillsheet?.output?.random3Id && engineer?.skillsheetBox?.[0]?.skillsheet?.output?.random4Id;
                            
                            const linkPath = hasSkillsheet && `${viewPath}/${engineer.skillsheetBox[0].skillsheet.output.random3Id}/${engineer.skillsheetBox[0].skillsheet.output.random4Id}`
                                
                            return (
                                <div className={sty['list-pc__tr-d']} key={engineer?.email || `engineer-${index}`}>
                                    <div className={sty['list-pc__td']}>{getDisplayName(engineer)}</div>
                                    <div className={sty['list-pc__td']}>
                                        <div>{engineer?.profile?.job_title || '未登録'}</div>
                                        {engineer?.profile?.job_title2 && <div>{engineer?.profile?.job_title2}</div>}
                                    </div>
                                    <div className={`${sty['list-pc__td']} ${sty['skill-tag-container']}`}>
                                        {(engineer?.profile?.skillTags|| []).length > 0 ? (
                                            (engineer?.profile?.skillTags|| []).map((skill, skillIndex) => (
                                                <div key={`${engineer?.email || 'unknown'}-skill-${skillIndex}-${skill}`} className={sty['skill-tag']}>
                                                    {skill}
                                                </div>
                                            ))
                                        ) : (
                                            '未登録'
                                        )}
                                    </div>
                                    <div className={sty['list-pc__td']}>
                                        {getLatestUpdateDate(engineer) ?
                                            dayjs(getLatestUpdateDate(engineer)).format('YYYY/MM/DD HH:mm') :
                                            '未登録'
                                        }
                                    </div>
                                    {/* ミニマムリリースでは、スキルシートは1つしか登録しないため、スキルシート1つ目のランダムIDを取得する */}
                                    {/* スキルシートが存在する場合のみリンクを表示 */}
                                    {hasSkillsheet && 
                                        <Link to={linkPath} target="_blank" rel="noreferrer noopener"></Link>
                                    }
                                </div>
                            );
                        })
                    ) : (
                        <div className={sty['list-pc__no-engineers']}>
                            {appliedTags.length > 0 ? '検索条件に一致するエンジニアが見つかりません' : 'エンジニアが登録されていません'}
                        </div>
                    )}
                </div>
            </div>

            <div className={sty['list-sp']}>
                <div className={sty['list-sp__cards']}>
                    {isLoading ? (
                        <div style={{ textAlign: 'center', padding: '40px 0', fontSize: '1.2em' }}>
                            Loading...
                        </div>
                    ) : filteredData.length > 0 ? (
                        filteredData.map((engineer, index) => {
                            const hasSkillsheet = engineer?.skillsheetBox?.[0]?.skillsheet?.output?.random3Id && engineer?.skillsheetBox?.[0]?.skillsheet?.output?.random4Id;
                            
                            const linkPath = hasSkillsheet && `${viewPath}/${engineer.skillsheetBox[0].skillsheet.output.random3Id}/${engineer.skillsheetBox[0].skillsheet.output.random4Id}`

                            return (
                                <div key={engineer?.email || `engineer-sp-${index}`} className={sty['list-sp__card']} >
                                    <div className={sty['list-sp__card-header']}>
                                        <div className={sty['list-sp__profile']}>
                                            <div className={`${sty['list-sp__name']}`}>
                                                {getDisplayName(engineer)}
                                            </div>
                                        </div>
                                    </div>
                                    <div className={sty['list-sp__card-content']}>
                                        <div className={`${sty['list-sp__job-title']}`}>
                                            <div>{engineer?.profile?.job_title || '未登録'}</div>
                                            {engineer?.profile?.job_title2 && <div>{engineer?.profile?.job_title2}</div>}
                                        </div>
                                        <div className={`${sty['list-sp__skill-tags']}`}>
                                            {(engineer?.profile?.skillTags|| []).length > 0 ? (
                                                (engineer?.profile?.skillTags|| []).map((skill, skillIndex) => (
                                                    <div key={`${engineer?.email || 'unknown'}-skill-sp-${skillIndex}-${skill}`} className={sty['skill-tag']}>
                                                        {skill}
                                                    </div>
                                                ))
                                            ) : (
                                                '未登録'
                                            )}
                                        </div>
                                        <div className={`${sty['list-sp__updated-date']}`}>
                                            <img src="/images/update.svg" alt="更新日時" className={sty['list-icon']} />
                                            {getLatestUpdateDate(engineer) ?
                                                dayjs(getLatestUpdateDate(engineer)).format('YYYY/MM/DD HH:mm') :
                                                '未登録'
                                            }
                                        </div>
                                    </div>
                                     {/* ミニマムリリースでは、スキルシートは1つしか登録しないため、スキルシート1つ目のランダムIDを取得する */}
                                    {/* スキルシートが存在する場合のみリンクを表示 */}
                                    {hasSkillsheet && 
                                        <Link to={linkPath} target="_blank" rel="noreferrer noopener"></Link>
                                    }
                                </div>
                            )
                        })
                    ) : (
                        <div className={sty['list__no-engineers']}>
                            {appliedTags.length > 0 ? '検索条件に一致するエンジニアが見つかりません' : 'エンジニアが登録されていません'}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default EngineerListByLoggedInAsEngineer;